import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateFormData,
  calculateInterest,
} from "../redux/actions/investmentForm";
import {
  selectFormData,
  selectCurrency,
  selectCalculatedInterest,
} from "../redux/selectors/investmentFormSelectors";
import useInvestmentProductRate from "./useInvestmentProductRate";
import {
  DepositType,
  isDynamicDeposit,
  toDepositTypeId,
} from "../helpers/depositType";

/** How long to wait after the last keystroke before re-estimating interest. */
const RECALCULATE_DELAY_MS = 500;

/**
 * Custom hook to handle investment duration terms logic
 */
export const useInvestmentDurationTerms = (
  form: any,
  tenantId?: number,
  investRateId?: number,
  data?: any,
) => {
  const dispatch = useDispatch();

  // Get data from Redux
  const formData = useSelector(selectFormData);
  const calculatedInterest = useSelector(selectCalculatedInterest);
  const currencyCode = useSelector(selectCurrency) || "NGN";
  const recalculateTimer = useRef<ReturnType<typeof setTimeout>>();

  // The detail for the currency being booked. It used to be index 0, which is
  // a different deposit type whenever the product's currencies differ.
  const productDetail =
    data?.investment_product_details?.find(
      (detail: any) => detail?.currency_code === formData.currency,
    ) ?? data?.investment_product_details?.[0];

  const interestPaymentTypeId =
    productDetail?.investment_interest_payment_type?.id;

  // Only a dynamic deposit honours a typed rate; for anything else the backend
  // ignores it and books the chart's.
  const canOverrideRate = isDynamicDeposit(productDetail?.deposit_type);
  const isOverridingRate =
    canOverrideRate && Boolean(formData.override_chart_interest);

  // Initialize form with values from redux when component mounts

  useEffect(() => {
    if (formData) {
      form.setFieldsValue({
        tenure: formData.tenure,
        interest_rate: formData.interest_rate,
        interest_repayment_type: interestPaymentTypeId,
        number_of_days_in_year: formData.number_of_days_in_year || 365,
        override_chart_interest: isOverridingRate,
      });
    }
  }, [form, formData]);

  // Calculate interest on mount
  useEffect(() => {
    dispatch(calculateInterest());
  }, [dispatch]);

  // Handle tenure change
  const handleTenureChange = (value: number | null) => {
    if (value === null || value === undefined) return;

    // Ensure we have a number
    const numValue = Number(value);

    // Update Redux state
    dispatch(
      updateFormData({
        tenure: numValue,
        interest_repayment_type: interestPaymentTypeId,
      }),
    );

    // Set form value
    form.setFieldsValue({ tenure: numValue });
  };

  // Handle days in year change
  const handleDaysInYearChange = (value: number) => {
    dispatch(updateFormData({ number_of_days_in_year: value }));
    form.setFieldsValue({ number_of_days_in_year: value });
  };

  // Handle interest repayment type change
  const handleInterestRepaymentTypeChange = (e: any) => {
    const value = e.target.value;
    dispatch(updateFormData({ interest_repayment_type: value }));
    dispatch(calculateInterest());
  };

  // Get current tenure from Redux to ensure we use the most up-to-date value
  const currentTenure = formData.tenure || form.getFieldValue("tenure") || 0;
  const currentPrincipalAmount =
    formData.principal_amount || form.getFieldValue("principal_amount") || 0;

  // Get investment product rate with loading state
  const {
    data: investmentProductRate,
    isLoading: isLoadingInterestRate,
    isFetching: isFetchingInterestRate,
  } = useInvestmentProductRate({
    investRateId: investRateId || 0,
    enteredPrincipalAmount: currentPrincipalAmount,
    tenure: currentTenure,
    tenantId: tenantId || 0,
    depositType:
      toDepositTypeId(productDetail?.deposit_type) ?? DepositType.FixedDeposit,
  });

  const fetchedInterestRate =
    investmentProductRate?.data?.depositProductInterestRate?.interest;

  // Set interest rate from API and recalculate interest when rate changes.
  // While the admin is overriding it, the typed rate stands.
  useEffect(() => {
    if (isOverridingRate || fetchedInterestRate === undefined) {
      return;
    }

    // Only update if the interest rate has actually changed
    const currentInterestRate = form.getFieldValue("interest_rate");
    if (currentInterestRate !== fetchedInterestRate) {
      form.setFieldsValue({ interest_rate: fetchedInterestRate });
      dispatch(updateFormData({ interest_rate: fetchedInterestRate }));

      // Calculate interest after a short delay to ensure all form data is updated
      setTimeout(() => {
        dispatch(calculateInterest());
      }, 100);
    }
  }, [fetchedInterestRate, isOverridingRate, form, dispatch]);

  useEffect(() => () => clearTimeout(recalculateTimer.current), []);

  // Turning the override off puts the chart's rate back.
  const handleOverrideChange = (checked: boolean) => {
    dispatch(updateFormData({ override_chart_interest: checked }));
    form.setFieldsValue({ override_chart_interest: checked });

    if (!checked && fetchedInterestRate !== undefined) {
      form.setFieldsValue({ interest_rate: fetchedInterestRate });
      dispatch(updateFormData({ interest_rate: fetchedInterestRate }));
      dispatch(calculateInterest());
    }
  };

  const handleInterestRateChange = (value: number | null) => {
    dispatch(updateFormData({ interest_rate: value ?? 0 }));

    clearTimeout(recalculateTimer.current);
    recalculateTimer.current = setTimeout(() => {
      if (value) {
        dispatch(calculateInterest());
      }
    }, RECALCULATE_DELAY_MS);
  };

  return {
    calculatedInterest,
    currencyCode,
    handleTenureChange,
    handleDaysInYearChange,
    handleInterestRepaymentTypeChange,
    handleOverrideChange,
    handleInterestRateChange,
    canOverrideRate,
    isOverridingRate,
    hasCalculatedInterest: !!calculatedInterest.interest_calculated,
    isLoadingInterestRate,
    isFetchingInterestRate,
    isCalculating: isLoadingInterestRate || isFetchingInterestRate,
  };
};

export default useInvestmentDurationTerms;
